import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription, finalize } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { BankStorageKindEnum, MoneyTypeEnum } from 'src/enums';
import { Bank, Warehouse } from 'src/typings';

import { BankService } from '../bank.service';

type PaymentChannelGroup = {
  moneyType: MoneyTypeEnum;
  banks: Bank[];
};

@Component({
  selector: 'app-bank-multi-selector',
  templateUrl: './multi-selector.component.html',
  styleUrls: ['./multi-selector.component.scss']
})
export class MultiSelectorComponent implements OnInit, OnDestroy {
  @Input() control: UntypedFormControl = new UntypedFormControl([]);

  @Input() userId: number | null = null;

  @Input() defaultWarehouseControl: UntypedFormControl | null = null;

  readonly paymentChannelControl = new UntypedFormControl([]);

  readonly cashboxWarehouseControl = new UntypedFormControl(null);

  bankList: Bank[] = [];

  paymentChannelGroups: PaymentChannelGroup[] = [];

  ownCashboxes: Bank[] = [];

  unsupportedSelectedBanks: Bank[] = [];

  selectedPaymentSummary = 'Не выбраны';

  loading = false;

  creatingCashbox = false;

  createCashboxOpened = false;

  private syncingControls = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly bankService: BankService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.control.valueChanges.subscribe(() => {
        if (!this.syncingControls) this.syncPaymentChannelsFromMainControl();
      })
    );
    this.subscriptions.add(
      this.paymentChannelControl.valueChanges.subscribe((value) => {
        if (!this.syncingControls) this.applyPaymentChannelSelection((value || []) as Bank[]);
      })
    );
    if (this.defaultWarehouseControl) {
      this.cashboxWarehouseControl.setValue(this.defaultWarehouseControl.value || null, {
        emitEvent: false
      });
      this.subscriptions.add(
        this.defaultWarehouseControl.valueChanges.subscribe((warehouse) => {
          if (!this.createCashboxOpened) {
            this.cashboxWarehouseControl.setValue(warehouse || null, { emitEvent: false });
          }
        })
      );
    }

    this.loadBanks();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private buildPaymentChannelGroups(): PaymentChannelGroup[] {
    const orderedTypes = [
      MoneyTypeEnum.terminal,
      MoneyTypeEnum.card,
      MoneyTypeEnum.bn,
      MoneyTypeEnum.A
    ];
    return orderedTypes
      .map((moneyType) => ({
        moneyType,
        banks: this.bankList
          .filter(
            (bank) =>
              bank.storageKind === BankStorageKindEnum.PAYMENT_CHANNEL &&
              bank.moneyType === moneyType
          )
          .sort((left, right) => left.description.localeCompare(right.description, 'ru'))
      }))
      .filter((group) => group.banks.length > 0);
  }

  private buildOwnCashboxes(): Bank[] {
    const userId = Number(this.userId || 0);
    if (!userId) return [];
    return this.bankList
      .filter(
        (bank) =>
          bank.storageKind === BankStorageKindEnum.MANAGER_CASHBOX &&
          Number(bank.ownerUser?.id || 0) === userId
      )
      .sort((left, right) => {
        const leftWarehouse = left.warehouse?.description || '';
        const rightWarehouse = right.warehouse?.description || '';
        return (
          leftWarehouse.localeCompare(rightWarehouse, 'ru') ||
          left.description.localeCompare(right.description, 'ru')
        );
      });
  }

  private buildUnsupportedSelectedBanks(): Bank[] {
    return this.selectedBanks.filter((bank) => {
      if (
        bank.storageKind === BankStorageKindEnum.PAYMENT_CHANNEL &&
        bank.moneyType !== MoneyTypeEnum.nal
      ) {
        return false;
      }
      return !this.isOwnCashbox(bank);
    });
  }

  private buildSelectedPaymentSummary(): string {
    const selected = (this.paymentChannelControl.value || []) as Bank[];
    if (!selected.length) return 'Не выбраны';
    const types = Array.from(new Set(selected.map((bank) => bank.moneyType)));
    return types.length === 1 && selected.length === 1
      ? `${types[0]} · ${selected[0].description}`
      : `${types.join(', ')} · каналов: ${selected.length}`;
  }

  get canCreateCashbox(): boolean {
    const warehouse = this.cashboxWarehouseControl.value as Warehouse | null;
    return Boolean(this.userId && warehouse?.id && !this.creatingCashbox);
  }

  isSelected(bank: Bank): boolean {
    return this.selectedBanks.some((item) => Number(item.id) === Number(bank.id));
  }

  toggleCashbox(bank: Bank, selected: boolean): void {
    if (bank.isOperational === false) return;
    const banks = this.selectedBanks.filter((item) => Number(item.id) !== Number(bank.id));
    if (selected) banks.push(bank);
    this.setMainControlValue(banks);
  }

  removeUnsupportedBank(bank: Bank): void {
    this.setMainControlValue(
      this.selectedBanks.filter((item) => Number(item.id) !== Number(bank.id))
    );
  }

  toggleCreateCashbox(): void {
    if (!this.userId) {
      this.notificationService.warning('Сначала сохраните нового пользователя');
      return;
    }
    this.createCashboxOpened = !this.createCashboxOpened;
    if (this.createCashboxOpened && !this.cashboxWarehouseControl.value) {
      this.cashboxWarehouseControl.setValue(this.defaultWarehouseControl?.value || null, {
        emitEvent: false
      });
    }
  }

  createCashbox(): void {
    const warehouse = this.cashboxWarehouseControl.value as Warehouse | null;
    if (!this.userId || !warehouse?.id || this.creatingCashbox) {
      this.notificationService.warning('Выберите точку для денежного ящика');
      return;
    }

    this.creatingCashbox = true;
    this.bankService
      .ensureManagerCashboxForUser(this.userId, Number(warehouse.id))
      .pipe(finalize(() => (this.creatingCashbox = false)))
      .subscribe({
        next: (cashbox) => {
          this.bankList = [
            ...this.bankList.filter((bank) => Number(bank.id) !== Number(cashbox.id)),
            cashbox
          ];
          const selected = this.selectedBanks.filter(
            (bank) => Number(bank.id) !== Number(cashbox.id)
          );
          selected.push(cashbox);
          this.setMainControlValue(selected);
          this.createCashboxOpened = false;
          this.notificationService.notice(`Денежный ящик «${cashbox.description}» подключен`);
        },
        error: (error) => {
          this.notificationService.warning(
            error?.error?.message || error?.message || 'Не удалось создать денежный ящик'
          );
        }
      });
  }

  channelMeta(bank: Bank): string {
    const parts = [bank.warehouse?.description || 'Без привязки к точке'];
    if (bank.isOperational === false) parts.push('Неактивен');
    return parts.join(' · ');
  }

  cashboxMeta(bank: Bank): string {
    const parts = [bank.warehouse?.description || 'Точка не указана'];
    parts.push(bank.isOperational === false ? 'Неактивен' : 'Активен');
    return parts.join(' · ');
  }

  private get selectedBanks(): Bank[] {
    return ((this.control.value || []) as Bank[]).map((selected) => {
      return this.bankList.find((bank) => Number(bank.id) === Number(selected.id)) || selected;
    });
  }

  private loadBanks(): void {
    this.loading = true;
    this.bankService
      .getList()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (list) => {
          this.bankList = list || [];
          this.syncPaymentChannelsFromMainControl();
        },
        error: (error) => {
          this.notificationService.warning(
            error?.error?.message || error?.message || 'Не удалось загрузить денежные каналы'
          );
        }
      });
  }

  private syncPaymentChannelsFromMainControl(): void {
    const selectedIds = new Set(this.selectedBanks.map((bank) => Number(bank.id)));
    const paymentChannels = this.bankList.filter(
      (bank) =>
        selectedIds.has(Number(bank.id)) &&
        bank.storageKind === BankStorageKindEnum.PAYMENT_CHANNEL &&
        bank.moneyType !== MoneyTypeEnum.nal
    );
    this.syncingControls = true;
    this.paymentChannelControl.setValue(paymentChannels, { emitEvent: false });
    this.syncingControls = false;
    this.rebuildViews();
  }

  private applyPaymentChannelSelection(paymentChannels: Bank[]): void {
    const preserved = this.selectedBanks.filter(
      (bank) =>
        bank.storageKind !== BankStorageKindEnum.PAYMENT_CHANNEL ||
        bank.moneyType === MoneyTypeEnum.nal
    );
    this.setMainControlValue([...preserved, ...paymentChannels]);
  }

  private setMainControlValue(banks: Bank[]): void {
    const uniqueBanks = Array.from(new Map(banks.map((bank) => [Number(bank.id), bank])).values());
    this.syncingControls = true;
    this.control.setValue(uniqueBanks);
    this.syncingControls = false;
    this.syncPaymentChannelsFromMainControl();
  }

  private rebuildViews(): void {
    this.paymentChannelGroups = this.buildPaymentChannelGroups();
    this.ownCashboxes = this.buildOwnCashboxes();
    this.unsupportedSelectedBanks = this.buildUnsupportedSelectedBanks();
    this.selectedPaymentSummary = this.buildSelectedPaymentSummary();
  }

  private isOwnCashbox(bank: Bank): boolean {
    return (
      bank.storageKind === BankStorageKindEnum.MANAGER_CASHBOX &&
      Number(bank.ownerUser?.id || 0) === Number(this.userId || 0)
    );
  }
}
